import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CheckBox, NumberBox, TagBox } from 'devextreme-react';
import { ScrollView } from 'devextreme-react/scroll-view';
import { Button } from 'devextreme-react/button';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import { useApiMicrobiology } from '../../../api/api.microbiology';
import { SysI_MicrobiologiaConfiguracion, SysI_MicrobiologiaAntibioticosCatalogo, SysI_MicrobiologiaMicroorganismosCatalogo } from '../../../types/types.microbiology';
import { newToast } from '../../../components';
import { TemplateConfigurationImproved } from './TemplateConfigurationImproved';

interface MicrobiologyConfigurationProps {
  IdAnalito: number;
  Analito: string;
}

export const MicrobiologyConfiguration: React.FC<MicrobiologyConfigurationProps> = ({ IdAnalito, Analito }) => {
  const { getConfiguracionAnalito, saveConfiguracionAnalito, getAntibioticos, getMicroorganismos } = useApiMicrobiology();

  const [loading, setLoading] = useState(false);
  const [antibioticos, setAntibioticos] = useState<SysI_MicrobiologiaAntibioticosCatalogo[]>([]);
  const [microorganismos, setMicroorganismos] = useState<SysI_MicrobiologiaMicroorganismosCatalogo[]>([]);
  const [usarPlantillaCultivo, setUsarPlantillaCultivo] = useState<boolean>(false);
  const [gramModalVisible, setGramModalVisible] = useState<boolean>(false);
  const [cultivoModalVisible, setCultivoModalVisible] = useState<boolean>(false);

  const [formData, setFormData] = useState<SysI_MicrobiologiaConfiguracion>({
    Mcf_An_Id: IdAnalito,
    Mcf_UsarPlantilla: false,
    Mcf_HabilitarGram: true,
    Mcf_PlantillaGram: '',
    Mcf_HabilitarCultivo: true,
    Mcf_PlantillaCultivo: '',
    Mcf_HabilitarIdentificacion: true,
    Mcf_HabilitarAntibiograma: true,
    Mcf_ReportarGramSiNegativo: false,
    Mcf_ReportarCultivoNegativoInmediato: false,
    Mcf_TiempoReportePreliminar1: 0,
    Mcf_TiempoReportePreliminar2: 0,
    Mcf_TiempoReporteFinal: 0,
    Mcf_PanelAntibioticosGramPositivos: '[]',
    Mcf_PanelAntibioticosGramNegativos: '[]',
  });

  // Cargar datos iniciales
  useEffect(() => {
    let isMounted = true;

    const loadInitialData = async () => {
      setLoading(true);
      try {
        const [config, antibioticosData, microorganismosData] = await Promise.all([getConfiguracionAnalito(IdAnalito), getAntibioticos(), getMicroorganismos()]);

        if (isMounted) {
          setAntibioticos(antibioticosData || []);
          setMicroorganismos(microorganismosData || []);

          if (config) {
            setFormData({
              ...config,
              Mcf_PlantillaCultivo: config.Mcf_PlantillaCultivo || '',
              Mcf_PanelAntibioticosGramPositivos: config.Mcf_PanelAntibioticosGramPositivos || '[]',
              Mcf_PanelAntibioticosGramNegativos: config.Mcf_PanelAntibioticosGramNegativos || '[]',
            });
            setUsarPlantillaCultivo(!!(config.Mcf_PlantillaCultivo && config.Mcf_PlantillaCultivo.trim()));
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error cargando datos:', error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadInitialData();

    // Cleanup
    return () => {
      isMounted = false;
    };
  }, [IdAnalito]);

  // Lista plana de antibióticos para TagBox
  const antibioticosOptions = useMemo(() => {
    if (!antibioticos || antibioticos.length === 0) return [];

    return antibioticos
      .filter((ab) => ab.Abc_Estado === true)
      .sort((a, b) => (a.Abc_OrdenPresentacion || 0) - (b.Abc_OrdenPresentacion || 0))
      .map((ab) => ({
        id: ab.Abc_Id,
        text: `${ab.Abc_Nombre} (${ab.Abc_FamiliaAntibiotico || 'Sin familia'})`,
        familia: ab.Abc_FamiliaAntibiotico,
      }));
  }, [antibioticos]);

  // Valida que la plantilla del cultivo sea un arreglo JSON con los nodos obligatorios
  const validateCultivoTemplate = useCallback((jsonStr: string | undefined): { valid: boolean; error?: string } => {
    if (!jsonStr || !jsonStr.trim()) {
      return { valid: false, error: 'La plantilla del cultivo está vacía' };
    }
    let parsed: any;
    try {
      parsed = JSON.parse(jsonStr);
    } catch {
      return { valid: false, error: 'El JSON de la plantilla del cultivo no tiene un formato válido' };
    }
    if (!Array.isArray(parsed)) {
      return { valid: false, error: 'La plantilla del cultivo debe ser un arreglo (array) de campos' };
    }
    const keys = parsed.map((f: any) => (f && typeof f.KeyValue === 'string' ? f.KeyValue : ''));
    const missing: string[] = [];
    if (!keys.includes('TipoMuestra')) missing.push('TipoMuestra');
    if (!keys.includes('TiempoNegatividad')) missing.push('TiempoNegatividad');
    if (missing.length > 0) {
      return { valid: false, error: `Falta(n) el/los nodo(s) obligatorio(s) en la plantilla del cultivo: ${missing.join(', ')}` };
    }
    return { valid: true };
  }, []);

  const handleSave = useCallback(async () => {
    setLoading(true);
    try {
      if (formData.Mcf_UsarPlantilla && formData.Mcf_PlantillaGram) {
        try {
          JSON.parse(formData.Mcf_PlantillaGram);
        } catch (error) {
          newToast('ERROR', 'El JSON de la plantilla no es válido. Por favor corrígelo antes de guardar.', 5);
          setLoading(false);
          return;
        }
      }

      // Validar plantilla del cultivo si está activa la opción "Configurar plantilla"
      if (formData.Mcf_HabilitarCultivo && usarPlantillaCultivo) {
        const result = validateCultivoTemplate(formData.Mcf_PlantillaCultivo);
        if (!result.valid) {
          newToast('ERROR', result.error || 'La plantilla del cultivo no es válida', 5);
          setLoading(false);
          return;
        }
      }

      await saveConfiguracionAnalito(formData);
    } catch (error) {
      console.error('Error guardando configuración:', error);
    } finally {
      setLoading(false);
    }
  }, [formData, usarPlantillaCultivo, saveConfiguracionAnalito, validateCultivoTemplate]);

  const handleFieldChange = useCallback((field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const getPanelValue = useCallback((panelJson: string | undefined) => {
    if (!panelJson) return [];
    try {
      const parsed = JSON.parse(panelJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, []);

  // El panel de antibióticos se configura una sola vez: la misma selección se
  // guarda tanto para Gram positivos como para Gram negativos (el backend
  // mantiene ambos campos, pero desde la UI se manejan juntos).
  const setBothPanels = useCallback((value: any[]) => {
    const arrayValue = Array.isArray(value) ? value : [];
    const jsonString = JSON.stringify(arrayValue);

    setFormData((prev) => {
      if (prev.Mcf_PanelAntibioticosGramPositivos === jsonString && prev.Mcf_PanelAntibioticosGramNegativos === jsonString) {
        return prev; // No actualizar si es el mismo valor
      }
      return {
        ...prev,
        Mcf_PanelAntibioticosGramPositivos: jsonString,
        Mcf_PanelAntibioticosGramNegativos: jsonString,
      };
    });
  }, []);

  const selectAllAntibiotics = useCallback(() => {
    setBothPanels(antibioticosOptions.map((o) => o.id));
  }, [antibioticosOptions, setBothPanels]);

  return (
    <div className='microbiology-configuration'>
      <div className='config-header'>
        <h3>Configuración de Microbiología</h3>
        <p className='config-subtitle'>{Analito}</p>
      </div>

      <TabPanel height={'70%'} animationEnabled swipeEnabled={false}>
        {/* PESTAÑA 1: COMPONENTES HABILITADOS */}
        <TabItem title='Componentes' icon='check'>
          <ScrollView height='100%' showScrollbar='onScroll'>
            <div className='config-section'>
              <div className='section-header'>
                <i className='dx-icon-check'></i>
                <h4>¿Qué componentes se reportarán?</h4>
              </div>

              <div className='components-grid'>
                <div className='component-card'>
                  <CheckBox value={formData.Mcf_HabilitarGram} onValueChanged={(e) => handleFieldChange('Mcf_HabilitarGram', e.value)} text='Examen Directo (Gram)' />
                  <p className='component-description'>Observación microscópica inicial de la muestra</p>

                  {formData.Mcf_HabilitarGram && (
                    <>
                      <div className='sub-option'>
                        <CheckBox value={formData.Mcf_ReportarGramSiNegativo} onValueChanged={(e) => handleFieldChange('Mcf_ReportarGramSiNegativo', e.value)} text='Reportar aunque sea negativo' />
                      </div>

                      {/* NUEVA OPCIÓN: Usar Plantilla */}
                      <div className='sub-option' style={{ marginTop: '12px' }}>
                        <CheckBox value={formData.Mcf_UsarPlantilla} onValueChanged={(e) => handleFieldChange('Mcf_UsarPlantilla', e.value)} text='Usar plantilla para Gram' />
                      </div>
                    </>
                  )}
                </div>

                <div className='component-card'>
                  <CheckBox value={formData.Mcf_HabilitarCultivo} onValueChanged={(e) => handleFieldChange('Mcf_HabilitarCultivo', e.value)} text='Cultivo' />
                  <p className='component-description'>Crecimiento en medio de cultivo</p>
                  {formData.Mcf_HabilitarCultivo && (
                    <>
                      <div className='sub-option'>
                        <CheckBox value={formData.Mcf_ReportarCultivoNegativoInmediato} onValueChanged={(e) => handleFieldChange('Mcf_ReportarCultivoNegativoInmediato', e.value)} text='Reportar cultivo negativo inmediatamente' />
                      </div>

                      {/* NUEVA OPCIÓN: Configurar plantilla del Cultivo */}
                      <div className='sub-option' style={{ marginTop: '12px' }}>
                        <CheckBox value={usarPlantillaCultivo} onValueChanged={(e) => setUsarPlantillaCultivo(e.value)} text='Configurar plantilla' />
                      </div>
                    </>
                  )}
                </div>

                <div className='component-card'>
                  <CheckBox value={formData.Mcf_HabilitarIdentificacion} onValueChanged={(e) => handleFieldChange('Mcf_HabilitarIdentificacion', e.value)} text='Identificación' />
                  <p className='component-description'>Identificación del microorganismo aislado</p>
                </div>
                <div className='component-card'>
                  <CheckBox value={formData.Mcf_HabilitarAntibiograma} onValueChanged={(e) => handleFieldChange('Mcf_HabilitarAntibiograma', e.value)} text='Antibiograma' />
                  <p className='component-description'>Prueba de sensibilidad a antibióticos</p>
                </div>
              </div>

              {/* BOTONES PARA ABRIR MODALES DE PLANTILLA JSON */}
              {((formData.Mcf_HabilitarGram && formData.Mcf_UsarPlantilla) || (formData.Mcf_HabilitarCultivo && usarPlantillaCultivo)) && (
                <div
                  className='template-buttons-row'
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    marginTop: '20px',
                    padding: '16px 20px',
                    background: '#f7fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                  }}>
                  {formData.Mcf_HabilitarGram && formData.Mcf_UsarPlantilla && (
                    <Button
                      text='Editar plantilla JSON del Gram'
                      icon='edit'
                      type='default'
                      stylingMode='contained'
                      onClick={() => setGramModalVisible(true)}
                    />
                  )}
                  {formData.Mcf_HabilitarCultivo && usarPlantillaCultivo && (
                    <Button
                      text='Editar plantilla JSON del Cultivo'
                      icon='edit'
                      type='default'
                      stylingMode='contained'
                      onClick={() => setCultivoModalVisible(true)}
                    />
                  )}
                </div>
              )}
            </div>
          </ScrollView>
        </TabItem>

        {/* PESTAÑA 2: TIEMPOS DE REPORTE */}
        <TabItem title='Tiempos' icon='clock'>
          <div className='config-section'>
            <div className='section-header'>
              <i className='dx-icon-clock'></i>
              <h4>Tiempos de reporte (en horas)</h4>
            </div>

            <div className='time-inputs'>
              <div className='time-card'>
                <label className='time-label'>
                  <i className='dx-icon-event'></i>
                  Reporte Preliminar 1
                </label>
                <NumberBox value={formData.Mcf_TiempoReportePreliminar1} onValueChanged={(e) => handleFieldChange('Mcf_TiempoReportePreliminar1', e.value)} placeholder='Ej: 18' min={0} step={1} showSpinButtons />
                <p className='time-hint'>Tiempo para el primer reporte parcial</p>
              </div>

              <div className='time-card'>
                <label className='time-label'>
                  <i className='dx-icon-event'></i>
                  Reporte Preliminar 2
                </label>
                <NumberBox value={formData.Mcf_TiempoReportePreliminar2} onValueChanged={(e) => handleFieldChange('Mcf_TiempoReportePreliminar2', e.value)} placeholder='Ej: 24' min={0} step={1} showSpinButtons />
                <p className='time-hint'>Tiempo para el segundo reporte parcial</p>
              </div>

              <div className='time-card'>
                <label className='time-label'>
                  <i className='dx-icon-check'></i>
                  Reporte Final
                </label>
                <NumberBox value={formData.Mcf_TiempoReporteFinal} onValueChanged={(e) => handleFieldChange('Mcf_TiempoReporteFinal', e.value)} placeholder='Ej: 48' min={0} step={1} showSpinButtons />
                <p className='time-hint'>Tiempo para el reporte definitivo</p>
              </div>
            </div>

            <div className='time-explanation'>
              <i className='dx-icon-info'></i>
              <p>Configure los tiempos estándar para el reporte de preliminares y finales</p>
            </div>
          </div>
        </TabItem>

        {/* PESTAÑA 3: PANELES DE ANTIBIÓTICOS */}
        <TabItem title='Antibiogramas' icon='fieldchooser'>
          <ScrollView height='100%' showScrollbar='onScroll'>
            <div className='config-section'>
              <div className='section-header'>
                <i className='dx-icon-fieldchooser'></i>
                <h4>Paneles predefinidos de antibióticos</h4>
              </div>

              <div className='single-panel-container'>
                <div className='panel-card'>
                  <div className='panel-header'>
                    <i className='dx-icon-detailslayout'></i>
                    <h5>Panel de antibióticos</h5>
                  </div>
                  <p className='panel-description'>Seleccione los antibióticos que se mostrarán por defecto en el antibiograma. La misma selección aplica para microorganismos Gram positivos y Gram negativos.</p>
                  <div className='panel-actions'>
                    <Button text='Seleccionar todos' icon='selectall' type='default' stylingMode='outlined' onClick={selectAllAntibiotics} disabled={antibioticosOptions.length === 0} />
                    <Button text='Quitar todos' icon='clear' stylingMode='text' onClick={() => setBothPanels([])} disabled={getPanelValue(formData.Mcf_PanelAntibioticosGramPositivos).length === 0} />
                  </div>
                  <TagBox dataSource={antibioticosOptions} displayExpr='text' valueExpr='id' value={getPanelValue(formData.Mcf_PanelAntibioticosGramPositivos)} onValueChanged={(e) => setBothPanels(e.value || [])} searchEnabled={true} showSelectionControls={true} applyValueMode='instantly' placeholder='Seleccione antibióticos...' height={250} noDataText='No hay antibióticos disponibles' />
                  <div className='panel-stats'>
                    <span className='stat-badge'>{getPanelValue(formData.Mcf_PanelAntibioticosGramPositivos).length} seleccionados</span>
                  </div>
                </div>
              </div>

              <div className='panel-hint'>
                <i className='dx-icon-info'></i>
                <p>Los antibióticos seleccionados se reportarán por defecto para este estudio, tanto en microorganismos Gram positivos como negativos.</p>
              </div>
            </div>
          </ScrollView>
        </TabItem>
      </TabPanel>

      {/* BOTÓN DE GUARDAR */}
      <div className='config-actions'>
        <Button text='GUARDAR CONFIGURACIÓN' type='default' onClick={handleSave} disabled={loading} width={250} />
      </div>

      {/* MODAL: PLANTILLA JSON DEL GRAM */}
      <Popup
        title='Plantilla JSON del Gram'
        visible={gramModalVisible}
        onHiding={() => setGramModalVisible(false)}
        showCloseButton
        dragEnabled={false}
        hideOnOutsideClick={false}
        width='95vw'
        height='90vh'>
        <div style={{ height: '100%' }}>
          {gramModalVisible && (
            <TemplateConfigurationImproved
              IdAnalito={IdAnalito}
              configPlantillas={formData.Mcf_PlantillaGram || ''}
              onSave={(json) => {
                handleFieldChange('Mcf_PlantillaGram', JSON.stringify(json));
                newToast('SUCCESS', 'Plantilla del Gram actualizada', 3);
                setGramModalVisible(false);
              }}
            />
          )}
        </div>
        <ToolbarItem toolbar='bottom' location='after'>
          <Button text='Cerrar' type='normal' stylingMode='outlined' onClick={() => setGramModalVisible(false)} />
        </ToolbarItem>
      </Popup>

      {/* MODAL: PLANTILLA JSON DEL CULTIVO */}
      <Popup
        title='Plantilla JSON del Cultivo'
        visible={cultivoModalVisible}
        onHiding={() => setCultivoModalVisible(false)}
        showCloseButton
        dragEnabled={false}
        hideOnOutsideClick={false}
        width='95vw'
        height='90vh'>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <p
            style={{
              margin: '0 0 8px 0',
              fontSize: '13px',
              color: '#4a5568',
            }}>
            La plantilla debe contener obligatoriamente los campos (KeyValue) <strong>TipoMuestra</strong>, <strong>ResultadoCultivo</strong> y <strong>TiempoNegatividad</strong>.
          </p>
          <div style={{ flex: 1, minHeight: 0 }}>
            {cultivoModalVisible && (
              <TemplateConfigurationImproved
                IdAnalito={IdAnalito}
                configPlantillas={formData.Mcf_PlantillaCultivo || ''}
                onSave={(json) => {
                  const jsonStr = JSON.stringify(json);
                  handleFieldChange('Mcf_PlantillaCultivo', jsonStr);
                  const result = validateCultivoTemplate(jsonStr);
                  if (result.valid) {
                    newToast('SUCCESS', 'Plantilla del cultivo actualizada', 3);
                  } else {
                    newToast('WARNING', result.error || 'Faltan nodos obligatorios en la plantilla del cultivo', 5);
                  }
                  setCultivoModalVisible(false);
                }}
              />
            )}
          </div>
        </div>
        <ToolbarItem toolbar='bottom' location='after'>
          <Button text='Cerrar' type='normal' stylingMode='outlined' onClick={() => setCultivoModalVisible(false)} />
        </ToolbarItem>
      </Popup>
    </div>
  );
};
